import {
  ETIQUETA_ESTADO_FINANCIACION,
  ETIQUETA_ESTADO_FINANCIACION_EN,
  type FilaDeFinanciacion,
  type ResumenDeFinanciaciones,
  type UsoDeFinanciador,
} from '@cnc/contracts';
import {useState} from 'react';
import {Link, useSearchParams} from 'react-router';
import {
  RUTA_EXCEL_DE_FINANCIACIONES,
  useCargarFinanciaciones,
  useFinanciaciones,
  useResumenDeFinanciaciones,
} from '../api/financiacion';
import {CargaExcelDeFinanciaciones} from '../componentes/carga-excel-financiaciones';
import {HistoriaDeCupos} from '../componentes/historia-de-cupos';
import {CajaAjustable} from '../componentes/caja-ajustable';
import {CampoDeArchivo} from '../componentes/elegir-archivo';
import {FechaSegun, REGLA_DESEMBOLSO, REGLA_PAGO_AL_FINANCIADOR} from '../componentes/fecha-segun';
import {AYUDA_FINANCIACIONES as A} from '../componentes/ayuda-exportaciones';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {PestanasSeguimiento} from '../componentes/pestanas-seguimiento';
import {SelectorMultiple} from '../componentes/selector-multiple';
import {usePermisos} from '../componentes/sesion-contexto';
import {ZonaAmpliable} from '../componentes/zona-ampliable';
import {et, tr} from '../i18n/idioma';
import {RangoDeLlegada, RangoDeZarpe} from '../componentes/rango-de-llegada';
import {fecha, fechaHora, mes, numero, textoOGuion} from '../utilidades/formato';

// Uso a partir del cual el cupo se marca como casi lleno.
const CASI_LLENO = 0.9;
const usd = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `USD ${numero(v)}`);

// Seguimiento → Financiaciones (decision 0021): el uso de cada cupo, su
// proyeccion mes a mes y la financiacion de cada exportacion. Sale del
// Status BLQ semanal y del Intercompany Export Chain.
export function PaginaFinanciaciones() {
  const resumen = useResumenDeFinanciaciones();
  const permisos = usePermisos();
  const [cargandoExcel, setCargandoExcel] = useState(false);
  return (
    <section className="space-y-5">
      <PestanasSeguimiento />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Financiaciones', 'Financing')}</h1>
          <p className="text-sm text-apagado">
            {tr(
              'Con quién se financia cada exportación al salir, cuánto se financió, cuándo paga el comprador y cuánto queda de cada cupo.',
              'Who finances each export at departure, how much was financed, when the buyer pays and how much of each credit line is left.',
            )}
            {resumen.data?.carga &&
              tr(
                ` Datos de ${resumen.data.carga.archivo ?? 'los archivos'}, cargados el ${fechaHora(resumen.data.carga.fecha)}.`,
                ` Data from ${resumen.data.carga.archivo ?? 'the files'}, loaded on ${fechaHora(resumen.data.carga.fecha)}.`,
              )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={RUTA_EXCEL_DE_FINANCIACIONES}
            download
            title={tr(
              'Una fila por exportación: lo del seguimiento en gris y lo que se trabaja en blanco. Se cambia en Excel y se vuelve a cargar.',
              'One row per export: tracking data in gray and what you work on in white. Change it in Excel and load it back.',
            )}
            className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
          >
            {tr('⬇ Excel para trabajar', '⬇ Excel to work on')}
          </a>
          {permisos.editar && (
            <button
              type="button"
              onClick={() => setCargandoExcel(x => !x)}
              className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
            >
              {tr('⬆ Cargar Excel de financiaciones', '⬆ Load financing Excel')}
            </button>
          )}
          {permisos.editar && (
            <Link
              to="/configuracion-de-campos/financiadores"
              className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
            >
              {tr('⚙ Financiadores y cupos', '⚙ Financers and credit lines')}
            </Link>
          )}
        </div>
      </div>
      {cargandoExcel && permisos.editar && <CargaExcelDeFinanciaciones onCerrar={() => setCargandoExcel(false)} />}
      {permisos.editar && <CargaDelStatus />}
      {resumen.isError && <p className="text-sm text-alerta">{resumen.error.message}</p>}
      {resumen.data && <Cupos resumen={resumen.data} />}
      <HistoriaDeCupos />
      {resumen.data && <Proyeccion resumen={resumen.data} />}
      <ZonaAmpliable titulo={tr('Financiaciones', 'Financing')}>
        <Tabla />
      </ZonaAmpliable>
    </section>
  );
}

// Respaldo: subir el Status BLQ (y el Intercompany) para traer lo que no se
// haya trabajado todavia en la app. Primero se revisa; lo editado en la app
// no se toca. Tambien se usa en Cargar archivos, ya abierta.
export function CargaDelStatus({abiertaAlInicio = false}: {abiertaAlInicio?: boolean}) {
  const [abierta, setAbierta] = useState(abiertaAlInicio);
  const [status, setStatus] = useState<File | null>(null);
  const [intercompany, setIntercompany] = useState<File | null>(null);
  const cargar = useCargarFinanciaciones();
  const r = cargar.data;
  const enviar = (modo: 'revisar' | 'aplicar') => status && cargar.mutate({status, intercompany, modo});
  if (!abierta)
    return (
      <button
        type="button"
        onClick={() => setAbierta(true)}
        className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
      >
        {tr('⬆ Cargar Status BLQ', '⬆ Load Status BLQ')}
      </button>
    );
  return (
    <div className="space-y-3 rounded-lg border border-linea bg-white p-4" data-prueba="carga-status">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-medium">{tr('Cargar Status BLQ', 'Load Status BLQ')}</h2>
          <p className="text-xs text-apagado">
            {tr(
              'Respaldo para traer lo que todavía no se haya trabajado en la app. Primero se revisa y nada se guarda hasta que confirmes. Lo editado en la app no se toca.',
              'Backup to bring in what has not been worked on in the app yet. It is reviewed first and nothing is saved until you confirm. What was edited in the app is not touched.',
            )}
          </p>
        </div>
        <button type="button" onClick={() => setAbierta(false)} className="text-apagado hover:text-tinta">
          ×
        </button>
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        <CampoDeArchivo
          etiqueta={tr('Status BLQ (.xlsx)', 'Status BLQ (.xlsx)')}
          accept=".xlsx"
          archivos={status ? [status] : []}
          onElegir={([a]) => {
            setStatus(a ?? null);
            cargar.reset();
          }}
        />
        <CampoDeArchivo
          etiqueta={tr('Intercompany Export Chain (opcional)', 'Intercompany Export Chain (optional)')}
          accept=".xlsx"
          archivos={intercompany ? [intercompany] : []}
          onElegir={([a]) => {
            setIntercompany(a ?? null);
            cargar.reset();
          }}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!status || cargar.isPending}
          onClick={() => enviar('revisar')}
          className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50"
        >
          {tr('Revisar', 'Review')}
        </button>
        {r && !r.aplicado && (
          <button
            type="button"
            disabled={cargar.isPending}
            onClick={() => enviar('aplicar')}
            className="rounded-md bg-verde-oscuro px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {tr('Confirmar y guardar', 'Confirm and save')}
          </button>
        )}
        {cargar.isPending && <span className="text-sm text-apagado">{tr('Leyendo…', 'Reading…')}</span>}
      </div>
      {cargar.isError && <p className="text-sm text-alerta">{cargar.error.message}</p>}
      {r && (
        <div className="space-y-1 text-sm" role="status">
          <p className="font-medium">
            {r.aplicado
              ? tr('Guardado.', 'Saved.')
              : tr('Revisión (todavía no se guardó nada):', 'Review (nothing saved yet):')}
          </p>
          <p>
            {tr(
              `${numero(r.exportaciones)} exportaciones (${Object.entries(r.porFinanciador)
                .map(([k, n]) => `${k} ${n}`)
                .join(
                  ', ',
                )}); ${numero(r.editadasEnLaApp)} editadas en la app no se tocan; ${numero(r.paraRevisar)} para revisar.`,
              `${numero(r.exportaciones)} exports (${Object.entries(r.porFinanciador)
                .map(([k, n]) => `${k} ${n}`)
                .join(
                  ', ',
                )}); ${numero(r.editadasEnLaApp)} edited in the app are not touched; ${numero(r.paraRevisar)} to review.`,
            )}
          </p>
          {r.queNoExisten.length > 0 && (
            <p className="text-apagado">
              {tr(
                `No están en la app (no se cargan): ${r.queNoExisten.join(', ')}.`,
                `Not in the app (not loaded): ${r.queNoExisten.join(', ')}.`,
              )}
            </p>
          )}
          {r.avisos.length > 0 && (
            <ul className="list-disc pl-5 text-xs">
              {r.avisos.slice(0, 20).map(a => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Barra({usado, cupo}: {usado: number; cupo: number}) {
  const parte = cupo > 0 ? Math.min(usado / cupo, 1) : 0;
  const lleno = cupo > 0 && usado / cupo >= CASI_LLENO;
  return (
    <div
      className="h-2 w-full overflow-hidden rounded-full bg-papel"
      role="meter"
      aria-valuemin={0}
      aria-valuemax={cupo}
      aria-valuenow={usado}
    >
      <div className={`h-full rounded-full ${lleno ? 'bg-alerta' : 'bg-verde'}`} style={{width: `${parte * 100}%`}} />
    </div>
  );
}

function Cupos({resumen}: {resumen: ResumenDeFinanciaciones}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-prueba="cupos">
      {/* Un cupo compartido es una sola tarjeta: BLQ + BLQM (Daniel, 13/10/2026). */}
      {resumen.financiadores
        .filter(f => !f.comparteCupoCon)
        .map(f => (
          <Cupo key={f.codigo} f={f} />
        ))}
    </div>
  );
}

function Cupo({f}: {f: UsoDeFinanciador}) {
  const permisos = usePermisos();
  const lleno = f.cupoUsd !== null && f.cupoUsd > 0 && f.usadoUsd / f.cupoUsd >= CASI_LLENO;
  return (
    <div className="rounded-lg border border-linea bg-white p-4" data-prueba={`cupo-${f.codigo}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-medium text-verde-oscuro">{[f.nombre, ...f.incluye].join(' + ')}</div>
        <div className="text-xs text-apagado">{[f.codigo, ...f.incluye].join(' + ')}</div>
      </div>
      {f.financia ? (
        <>
          <div className="mt-2 text-2xl font-semibold tabular-nums">{usd(f.usadoUsd)}</div>
          <div className="text-xs text-apagado">
            {tr('financiado y sin pagar por el comprador', 'financed and not yet paid by the buyer')}
            <NotaDeAyuda texto={A.usado} />
          </div>
          {f.comparteCupoCon ? (
            <p className="mt-3 text-xs text-apagado" data-prueba="comparte-cupo">
              {tr(
                `Usa el cupo de ${f.comparteCupoCon}: lo suyo ya está sumado allá.`,
                `Uses the ${f.comparteCupoCon} line: its amount is already added there.`,
              )}
            </p>
          ) : f.cupoUsd !== null ? (
            <div className="mt-3 space-y-1">
              <Barra usado={f.usadoUsd} cupo={f.cupoUsd} />
              <div className="flex justify-between text-xs">
                <span className="text-apagado">
                  {tr(`Cupo ${usd(f.cupoUsd)}`, `Line ${usd(f.cupoUsd)}`)} ·{' '}
                  {numero((f.usadoUsd / (f.cupoUsd || 1)) * 100)} %
                  <NotaDeAyuda texto={A.cupo} />
                </span>
                <span className={lleno ? 'font-medium text-alerta' : 'text-apagado'}>
                  {lleno && '⚠ '}
                  {tr(`Disponible ${usd(f.disponibleUsd)}`, `Available ${usd(f.disponibleUsd)}`)}
                </span>
              </div>
              {f.incluye.length > 0 && (
                <div className="text-xs text-apagado" data-prueba="cupo-incluye">
                  {tr(
                    `${f.codigo} ${usd(f.usadoPropioUsd)} + ${f.incluye.join(', ')} ${usd(f.usadoUsd - f.usadoPropioUsd)}`,
                    `${f.codigo} ${usd(f.usadoPropioUsd)} + ${f.incluye.join(', ')} ${usd(f.usadoUsd - f.usadoPropioUsd)}`,
                  )}
                  <NotaDeAyuda texto={A.incluye} />
                </div>
              )}
            </div>
          ) : (
            <p className="mt-3 text-xs text-apagado">{tr('Cupo por definir.', 'Credit line not defined yet.')}</p>
          )}
          {permisos.editar && (
            <Link
              to="/configuracion-de-campos/financiadores"
              className="mt-1 inline-block text-xs text-verde hover:underline"
            >
              {tr('Ajustar cupo', 'Adjust credit line')}
            </Link>
          )}
        </>
      ) : (
        <>
          <div className="mt-2 text-2xl font-semibold tabular-nums">{usd(f.porCobrarUsd)}</div>
          <div className="text-xs text-apagado">
            {tr('venta directa, pendiente de pago', 'direct sale, awaiting payment')}
            <NotaDeAyuda texto={A.ventaDirecta} />
          </div>
        </>
      )}
      <dl className="mt-3 space-y-0.5 text-xs">
        <div className="flex justify-between">
          <dt className="text-apagado">
            {tr('Exportaciones abiertas', 'Open exports')}
            <NotaDeAyuda texto={A.abiertas} />
          </dt>
          <dd className="tabular-nums">{numero(f.exportacionesAbiertas)}</dd>
        </div>
        {f.financia && (
          <div className="flex justify-between">
            <dt className="text-apagado">
              {tr('Por financiar', 'To be financed')}
              <NotaDeAyuda texto={A.porFinanciar} />
            </dt>
            <dd className="tabular-nums">{usd(f.porFinanciarUsd)}</dd>
          </div>
        )}
        {f.financia && (
          <div className="flex justify-between">
            <dt className="text-apagado">
              {tr('Por cobrar al comprador', 'Due from the buyer')}
              <NotaDeAyuda texto={A.porCobrar} />
            </dt>
            <dd className="tabular-nums">{usd(f.porCobrarUsd)}</dd>
          </div>
        )}
        {f.vencidoUsd > 0 && (
          <div className="flex justify-between font-medium text-alerta">
            <dt>
              ⚠ {tr('Pago vencido', 'Overdue payment')}
              <NotaDeAyuda texto={A.vencido} />
            </dt>
            <dd className="tabular-nums">{usd(f.vencidoUsd)}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

function Proyeccion({resumen}: {resumen: ResumenDeFinanciaciones}) {
  const conCupo = resumen.financiadores.filter(f => f.financia);
  return (
    <div className="rounded-lg border border-linea bg-white p-4" data-prueba="proyeccion">
      <h2 className="font-medium">{tr('Proyección de los próximos meses', 'Projection for the coming months')}</h2>
      <p className="mt-1 text-xs text-apagado">
        {tr(
          'Desembolsos esperados de los financiadores, pagos esperados de los compradores y el uso de cada cupo al cierre del mes. En BLQ las fechas salen del seguimiento: BLQ paga el día del zarpe y se le paga 5 días hábiles antes de la llegada a Mundra. Un pago vencido sigue ocupando el cupo hasta que llegue.',
          'Expected disbursements from financers, expected buyer payments and the use of each credit line at month end. For BLQ the dates come from the tracking: BLQ pays on the departure day and is paid 5 business days before arrival in Mundra. An overdue payment keeps using the line until it arrives.',
        )}
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-apagado">
              <th className="px-2 py-1 font-semibold">
                {tr('Mes', 'Month')}
                <NotaDeAyuda texto={A.mes} />
              </th>
              <th className="px-2 py-1 text-right font-semibold">
                {tr('Desembolsos', 'Disbursements')}
                <NotaDeAyuda texto={A.desembolsos} />
              </th>
              <th className="px-2 py-1 text-right font-semibold">
                {tr('Pagos de compradores', 'Buyer payments')}
                <NotaDeAyuda texto={A.pagosCompradores} />
              </th>
              {conCupo.map(f => (
                <th key={f.codigo} className="px-2 py-1 font-semibold">
                  {tr(`Uso ${f.codigo} al cierre`, `${f.codigo} use at month end`)}
                  <NotaDeAyuda texto={A.usoAlCierre} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {resumen.proyeccion.map(p => (
              <tr key={p.mes} className="border-t border-linea/70 align-middle">
                <td className="px-2 py-1.5">{mes(p.mes)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{usd(p.desembolsosUsd)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{usd(p.pagosCompradorUsd)}</td>
                {conCupo.map(f => {
                  const uso = p.usoAlCierre[f.codigo] ?? 0;
                  const lleno = f.cupoUsd !== null && f.cupoUsd > 0 && uso / f.cupoUsd >= CASI_LLENO;
                  return (
                    <td key={f.codigo} className="min-w-44 px-2 py-1.5">
                      <div className={`text-right tabular-nums ${lleno ? 'font-medium text-alerta' : ''}`}>
                        {lleno && '⚠ '}
                        {usd(uso)}
                      </div>
                      {f.cupoUsd !== null && <Barra usado={uso} cupo={f.cupoUsd} />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tabla() {
  const [parametros, setParametros] = useSearchParams();
  const consulta = useFinanciaciones(parametros);
  const x = consulta.data;
  const estados = et(ETIQUETA_ESTADO_FINANCIACION, ETIQUETA_ESTADO_FINANCIACION_EN);
  const cambiar = (clave: string, valor: string) => {
    const nuevos = new URLSearchParams(parametros);
    if (valor) nuevos.set(clave, valor);
    else nuevos.delete(clave);
    if (clave !== 'pagina') nuevos.delete('pagina');
    setParametros(nuevos, {replace: true});
  };
  const pagina = Number(parametros.get('pagina') ?? 1);
  const selector = (
    clave: 'financiador' | 'estado' | 'anio' | 'exportador',
    etiqueta: string,
    opciones: Array<{valor: string; etiqueta: string; cantidad?: number}> | undefined,
    ancho = 'w-44',
  ) => (
    <SelectorMultiple
      etiqueta={etiqueta}
      valor={parametros.get(clave) ?? undefined}
      opciones={opciones ?? []}
      onCambiar={v => cambiar(clave, v ?? '')}
      ayuda={A[clave]}
      clase={`flex ${ancho} flex-col gap-1 text-xs text-apagado`}
    />
  );
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-linea bg-white p-3 text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-apagado">
            {tr('Buscar', 'Search')}
            <NotaDeAyuda texto={A.buscar} dentroDeEtiqueta />
          </span>
          <input
            type="search"
            defaultValue={parametros.get('buscar') ?? ''}
            onKeyDown={e => e.key === 'Enter' && cambiar('buscar', e.currentTarget.value.trim())}
            onBlur={e =>
              e.currentTarget.value.trim() !== (parametros.get('buscar') ?? '') &&
              cambiar('buscar', e.currentTarget.value.trim())
            }
            placeholder={tr('Exportación, factura, BL…', 'Export, invoice, BL…')}
            className="w-56 rounded-md border border-linea px-2 py-1"
          />
        </label>
        {selector('financiador', tr('Financiador', 'Financer'), x?.opciones.financiadores, 'w-40')}
        {selector('estado', tr('Estado de la financiación', 'Financing status'), x?.opciones.estados, 'w-56')}
        {selector('anio', tr('Año de zarpe', 'Departure year'), x?.opciones.anios, 'w-36')}
        {selector('exportador', tr('Exportador', 'Exporter'), x?.opciones.exportadores)}
        <RangoDeZarpe parametros={parametros} cambiar={cambiar} />
        <RangoDeLlegada parametros={parametros} cambiar={cambiar} />
        <label className="flex items-center gap-2 pb-1">
          <input
            type="checkbox"
            checked={parametros.get('soloVencidas') === 'true'}
            onChange={e => cambiar('soloVencidas', e.target.checked ? 'true' : '')}
          />
          {tr('Solo pagos vencidos', 'Only overdue payments')}
          <NotaDeAyuda texto={A.soloVencidas} dentroDeEtiqueta />
        </label>
        {x && (
          <span className="ml-auto text-apagado">
            {tr(
              `${numero(x.total)} exportaciones · financiado ${usd(x.totales.financiadoUsd)}`,
              `${numero(x.total)} exports · financed ${usd(x.totales.financiadoUsd)}`,
            )}
          </span>
        )}
      </div>
      {consulta.isError && <p className="text-sm text-alerta">{consulta.error.message}</p>}
      {x && (
        <CajaAjustable className="overflow-auto rounded-lg border border-linea bg-white">
          <table className="w-full text-sm" data-prueba="financiaciones">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                <th className="px-3 py-2 font-semibold">
                  {tr('Exportación', 'Export')}
                  <NotaDeAyuda texto={A.exportacion} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Exportador', 'Exporter')}
                  <NotaDeAyuda texto={A.exportador} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Financiador', 'Financer')}
                  <NotaDeAyuda texto={A.financiador} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Estado', 'Status')}
                  <NotaDeAyuda texto={A.estado} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Zarpe', 'Departure')}
                  <NotaDeAyuda texto={A.zarpe} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('ETA Mundra', 'ETA Mundra')}
                  <NotaDeAyuda texto={A.etaMundra} />
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {tr('Financiado', 'Financed')}
                  <NotaDeAyuda texto={A.financiado} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Desembolso', 'Disbursement')}
                  <NotaDeAyuda texto={A.desembolso} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Paga al financiador', 'Pays the financer')}
                  <NotaDeAyuda texto={A.pagaAlFinanciador} />
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {tr('Paga el comprador', 'Buyer pays')}
                  <NotaDeAyuda texto={A.pagaComprador} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Fecha de pago', 'Payment date')}
                  <NotaDeAyuda texto={A.fechaPago} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Factura', 'Invoice')}
                  <NotaDeAyuda texto={A.factura} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Referencia', 'Reference')}
                  <NotaDeAyuda texto={A.referencia} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  BL
                  <NotaDeAyuda texto={A.bl} />
                </th>
                <th className="px-3 py-2 font-semibold">
                  {tr('Cadena', 'Chain')}
                  <NotaDeAyuda texto={A.cadena} />
                </th>
              </tr>
            </thead>
            <tbody>
              {x.elementos.map((f: FilaDeFinanciacion) => (
                <tr key={f.exportacion} className="border-t border-linea/70 align-top">
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <Link
                      to={`/seguimiento/${encodeURIComponent(f.exportacion)}#financiacion`}
                      className="font-medium text-verde hover:underline"
                    >
                      {f.exportacion}
                    </Link>
                    {f.paraRevisar && (
                      <span className="ml-1 cursor-help text-xs" title={f.nota ?? undefined}>
                        {tr('⚠ revisar', '⚠ review')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">{textoOGuion(f.exportador)}</td>
                  <td className="px-3 py-1.5">{textoOGuion(f.financiador)}</td>
                  <td className="px-3 py-1.5">{estados[f.estado]}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{fecha(f.zarpe)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{fecha(f.etaMundra)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">{usd(f.financiadoUsd)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                    {f.desembolso ? (
                      fecha(f.desembolso)
                    ) : f.desembolsoEsperado ? (
                      <span className="text-apagado">
                        {tr('esperado ', 'expected ')}
                        <FechaSegun valor={f.desembolsoEsperado} segun={f.desembolsoSegun} regla={REGLA_DESEMBOLSO()} />
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-1.5">{textoOGuion(f.pagaAlFinanciador)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">{usd(f.valorCompradorUsd)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                    {f.pagadoEl ? (
                      tr(`pagó ${fecha(f.pagadoEl)}`, `paid ${fecha(f.pagadoEl)}`)
                    ) : (
                      <span className={f.vencida ? 'font-medium text-alerta' : ''}>
                        {f.vencida && '⚠ '}
                        <FechaSegun
                          valor={f.pagoComprador}
                          segun={f.pagoCompradorSegun}
                          regla={REGLA_PAGO_AL_FINANCIADOR()}
                        />
                        {f.vencida && tr(' vencido', ' overdue')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">{textoOGuion(f.factura)}</td>
                  <td className="px-3 py-1.5">{textoOGuion(f.referencia)}</td>
                  <td className="px-3 py-1.5">{textoOGuion(f.bl)}</td>
                  <td className="px-3 py-1.5">{textoOGuion(f.cadena)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {x.elementos.length === 0 && (
            <p className="p-4 text-sm text-apagado">
              {tr(
                'No hay financiaciones con estos filtros. Si la lista está vacía, falta cargar el Status BLQ.',
                'No financing with these filters. If the list is empty, the Status BLQ has not been loaded yet.',
              )}
            </p>
          )}
        </CajaAjustable>
      )}
      {x && x.totalPaginas > 1 && (
        <div className="flex items-center justify-end gap-3 text-sm">
          <button
            type="button"
            disabled={pagina <= 1}
            onClick={() => cambiar('pagina', String(pagina - 1))}
            className="rounded-md border border-linea bg-white px-3 py-1 disabled:opacity-40"
          >
            {tr('← Anterior', '← Previous')}
          </button>
          <span className="text-apagado">
            {tr(`Página ${pagina} de ${x.totalPaginas}`, `Page ${pagina} of ${x.totalPaginas}`)}
          </span>
          <button
            type="button"
            disabled={pagina >= x.totalPaginas}
            onClick={() => cambiar('pagina', String(pagina + 1))}
            className="rounded-md border border-linea bg-white px-3 py-1 disabled:opacity-40"
          >
            {tr('Siguiente →', 'Next →')}
          </button>
        </div>
      )}
    </div>
  );
}
